export type SeoLandingPageStep = {
  description: string;
  title: string;
};

export type SeoLandingPageSection = {
  bullets?: string[];
  paragraphs: string[];
  title: string;
};

export type SeoLandingPageFaq = {
  answer: string;
  id: string;
  question: string;
};

export type SeoLandingPageDefinition = {
  cta: {
    buttonHref: string;
    buttonLabel: string;
    description: string;
    title: string;
  };
  description: string;
  faqs: SeoLandingPageFaq[];
  h1: string;
  intro: SeoLandingPageSection;
  path: string;
  primaryKeyword: string;
  problem: SeoLandingPageSection;
  relatedPaths: string[];
  solution: SeoLandingPageSection;
  steps: SeoLandingPageStep[];
  title: string;
};

export const seoLandingPageRegistry: Record<string, SeoLandingPageDefinition> = {
  '/sync-airtable-to-framer': {
    path: '/sync-airtable-to-framer',
    primaryKeyword: 'sync Airtable to Framer',
    title: 'Sync Airtable to Framer | FramerSync',
    description:
      'Sync Airtable to Framer CMS with field mapping, preview, schedules, and webhook-ready automation built for repeatable publishing.',
    h1: 'Sync Airtable to Framer CMS without manual exports',
    intro: {
      title: 'Move Airtable content into Framer on a repeatable workflow',
      paragraphs: [
        'Airtable is a common source of truth for directories, product content, and marketing databases, but moving that data into Framer CMS manually gets brittle fast.',
        'FramerSync gives you a repeatable Airtable to Framer workflow with source connection, field mapping, sync execution, and run history in one place.',
      ],
    },
    problem: {
      title: 'Why Airtable-to-Framer workflows usually break down',
      paragraphs: [
        'Manual exports and copy-paste updates slow down publishing and create gaps between the base your team edits and the Framer CMS content your site actually uses.',
      ],
      bullets: [
        'Content changes are easy to miss when updates depend on manual exports.',
        'Field shape mismatches become visible only after content lands in Framer.',
        'Teams lose visibility into what changed and when a sync failed.',
      ],
    },
    solution: {
      title: 'Use FramerSync as the connection layer',
      paragraphs: [
        'Connect the Airtable account you already use, select the base and table, preview incoming records, and map destination fields before running a live sync.',
      ],
      bullets: [
        'Connected Airtable source setup',
        'Collection-specific field mapping',
        'Manual, scheduled, or webhook-triggered syncs',
        'Run history for operational visibility',
      ],
    },
    steps: [
      {
        title: 'Connect Airtable',
        description:
          'Authorize Airtable, choose the base and table, and confirm the source records you want to send into Framer.',
      },
      {
        title: 'Map Airtable columns to Framer fields',
        description:
          'Preview the incoming data and align Airtable columns with the destination fields in your Framer CMS collection.',
      },
      {
        title: 'Launch or automate the sync',
        description:
          'Run the sync on demand or configure automation so Framer reflects Airtable updates on a schedule or webhook trigger.',
      },
    ],
    cta: {
      title: 'Start your Airtable to Framer sync setup',
      description:
        'Create an account, connect your project, and configure the Airtable-driven collection you want to keep current.',
      buttonLabel: 'Create your account',
      buttonHref: '/register',
    },
    faqs: [
      {
        id: 'airtable-faq-1',
        question: 'Can I choose a specific Airtable table for a Framer collection?',
        answer:
          'Yes. The Airtable connection flow is collection-specific, so you can target the exact base and table that should feed a given Framer CMS collection.',
      },
      {
        id: 'airtable-faq-2',
        question: 'Do I still get field mapping control with Airtable?',
        answer:
          'Yes. Airtable records still pass through preview and mapping, so you can decide exactly how those fields land in Framer.',
      },
    ],
    relatedPaths: ['/framer-cms-integration', '/how-to-sync-data-to-framer'],
  },
  '/sync-google-sheets-to-framer': {
    path: '/sync-google-sheets-to-framer',
    primaryKeyword: 'sync Google Sheets to Framer',
    title: 'Sync Google Sheets to Framer | FramerSync',
    description:
      'Sync Google Sheets to Framer CMS with sheet selection, preview, field mapping, and repeatable automation for operational content.',
    h1: 'Sync Google Sheets to Framer CMS on a repeatable workflow',
    intro: {
      title: 'Use the spreadsheet your team already updates',
      paragraphs: [
        'Google Sheets is often where teams manage launch calendars, partner lists, catalogs, and internal content staging before it needs to show up in Framer.',
        'FramerSync connects that spreadsheet workflow to Framer CMS so updates stop depending on manual handoffs.',
      ],
    },
    problem: {
      title: 'Why spreadsheet-based publishing creates drag',
      paragraphs: [
        'When Sheets is the source but Framer is the presentation layer, teams often end up re-entering the same information twice or waiting on ad hoc content pushes.',
      ],
      bullets: [
        'Manual updates create publishing lag.',
        'Spreadsheet structure changes can silently break downstream content.',
        'There is no run history when teams update Framer by hand.',
      ],
    },
    solution: {
      title: 'Sync the selected sheet into the right Framer collection',
      paragraphs: [
        'Connect Google Sheets, pick the spreadsheet and tab, preview the records, and map the columns to the Framer fields you want to maintain.',
      ],
      bullets: [
        'Spreadsheet and tab selection',
        'Preview-based field mapping',
        'Manual and scheduled sync support',
        'Operational run tracking',
      ],
    },
    steps: [
      {
        title: 'Connect the Google account',
        description:
          'Authorize the Google account that has access to the spreadsheet and choose the file and tab to use as the source.',
      },
      {
        title: 'Confirm the payload shape',
        description:
          'Review the records coming from the sheet, then map the relevant columns into your Framer CMS collection.',
      },
      {
        title: 'Keep Framer updated automatically',
        description:
          'Run the sync manually at first, then enable recurring automation once the mapping is validated.',
      },
    ],
    cta: {
      title: 'Connect a Google Sheet to Framer CMS',
      description:
        'Set up the spreadsheet-backed collection you want to keep in sync and move the workflow out of manual publishing.',
      buttonLabel: 'Start free trial',
      buttonHref: '/register',
    },
    faqs: [
      {
        id: 'sheets-faq-1',
        question: 'Can I target a specific tab in Google Sheets?',
        answer:
          'Yes. FramerSync lets you choose the spreadsheet and the exact sheet tab that should act as the source for a Framer CMS collection.',
      },
      {
        id: 'sheets-faq-2',
        question: 'Is Google Sheets useful for non-technical content teams?',
        answer:
          'Yes. It works well when the editing workflow already lives in Sheets and the site content needs to stay aligned in Framer without developer intervention.',
      },
    ],
    relatedPaths: ['/how-to-sync-data-to-framer', '/framer-cms-integration'],
  },
  '/framer-api-sync': {
    path: '/framer-api-sync',
    primaryKeyword: 'Framer API Sync',
    title: 'Framer API Sync | FramerSync',
    description:
      'Framer API Sync for external data sources with payload preview, field mapping, schedules, webhooks, and run history.',
    h1: 'Framer API Sync for external data that changes in real time',
    intro: {
      title: 'Use Framer as the presentation layer, not the source system',
      paragraphs: [
        'When your product, backend, or third-party service already exposes structured data, Framer API Sync gives you a route into Framer CMS without maintaining a custom import script.',
        'FramerSync focuses on the operational layer around the import, not just the fetch request itself.',
      ],
    },
    problem: {
      title: 'Why custom API imports become expensive',
      paragraphs: [
        'A one-off script might fetch data once, but production workflows also need validation, mapping, retries, automation, and visibility when the source changes shape.',
      ],
      bullets: [
        'Authentication, pagination, and payload paths vary by API.',
        'Teams need field mapping before a live content write.',
        'Run failures need a supportable trail, not just console output.',
      ],
    },
    solution: {
      title: 'Run API-driven Framer syncs from a managed workflow',
      paragraphs: [
        'FramerSync handles source testing, payload preview, field mapping, sync runs, and automation so your Framer CMS integration stays operationally manageable.',
      ],
      bullets: [
        'REST API source setup',
        'Bearer, Basic, and API key authentication support',
        'Pagination controls and payload path selection',
        'Run tracking for launched syncs',
      ],
    },
    steps: [
      {
        title: 'Test the API connection',
        description:
          'Confirm the endpoint, authentication, and payload shape before the configuration is saved.',
      },
      {
        title: 'Map the fields into Framer',
        description:
          'Use the previewed response to align source fields with the Framer collection fields that should receive updates.',
      },
      {
        title: 'Run and monitor the sync',
        description:
          'Launch the sync or automate it, then inspect run history when a payload, field, or connection change needs attention.',
      },
    ],
    cta: {
      title: 'Set up Framer API Sync',
      description:
        'Connect your Framer project and configure the external API collection you want to keep current.',
      buttonLabel: 'See the app',
      buttonHref: '/',
    },
    faqs: [
      {
        id: 'api-faq-1',
        question: 'What authentication methods does Framer API Sync support?',
        answer:
          'It supports unauthenticated requests, bearer tokens, basic auth, and API keys sent as headers or query parameters.',
      },
      {
        id: 'api-faq-2',
        question: 'Can it handle paginated APIs?',
        answer:
          'Yes. The API sync flow supports pagination controls so large datasets can be previewed and synced predictably.',
      },
    ],
    relatedPaths: ['/how-to-sync-data-to-framer', '/framer-cms-integration'],
  },
  '/how-to-sync-data-to-framer': {
    path: '/how-to-sync-data-to-framer',
    primaryKeyword: 'how to sync data to Framer',
    title: 'How to Sync Data to Framer | FramerSync',
    description:
      'Learn how to sync data to Framer CMS with a repeatable workflow for preview, field mapping, sync execution, and automation.',
    h1: 'How to sync data to Framer CMS without manual imports',
    intro: {
      title: 'A repeatable Framer sync workflow matters more than a one-time import',
      paragraphs: [
        'If you want Framer content to stay aligned with an external source, the job is not just moving rows once. It is building a workflow that can be rerun safely.',
        'FramerSync is designed around that repeatable workflow so setup, preview, mapping, and launch all happen in the same product.',
      ],
    },
    problem: {
      title: 'The usual content-sync pain points',
      paragraphs: [
        'Teams often know the source data exists, but they still struggle with how to get it into Framer consistently once schemas, schedules, and approval pressure show up.',
      ],
      bullets: [
        'The upstream source is structured differently than the Framer collection.',
        'Teams need confidence before a live update runs.',
        'The workflow needs to survive repeated updates, not just a one-time import.',
      ],
    },
    solution: {
      title: 'Use the same workflow every time',
      paragraphs: [
        'FramerSync lets you test the source, preview the payload, map the destination fields, save the configuration, and then launch or automate the sync.',
      ],
      bullets: [
        'Preview-first source validation',
        'Collection-level field mapping',
        'Support for manual, scheduled, and webhook syncs',
        'Run history to review outcomes',
      ],
    },
    steps: [
      {
        title: 'Connect the upstream source',
        description:
          'Choose the source type you want to use and make sure the response or source records can be previewed successfully.',
      },
      {
        title: 'Map fields into the destination collection',
        description:
          'Align the source data to the Framer fields that should receive updates and decide which fields are active in the sync.',
      },
      {
        title: 'Save, run, and automate',
        description:
          'Persist the configuration, run the first sync, then add schedule or webhook automation once the setup is stable.',
      },
    ],
    cta: {
      title: 'Turn your Framer import process into a system',
      description:
        'Use the same repeatable workflow for setup, validation, and sync launches instead of relying on ad hoc manual pushes.',
      buttonLabel: 'Create account',
      buttonHref: '/register',
    },
    faqs: [
      {
        id: 'howto-faq-1',
        question: 'What do I need before syncing data to Framer?',
        answer:
          'You need access to the Framer project, the destination collection, and the upstream source you want to connect. After that, the main task is validating the payload and mapping fields correctly.',
      },
      {
        id: 'howto-faq-2',
        question: 'Can I automate the sync after the first successful run?',
        answer:
          'Yes. Once the configuration is working, you can use schedule or webhook automation so repeat updates do not depend on manual launches.',
      },
    ],
    relatedPaths: ['/framer-api-sync', '/sync-google-sheets-to-framer'],
  },
  '/framer-cms-integration': {
    path: '/framer-cms-integration',
    primaryKeyword: 'Framer CMS integration',
    title: 'Framer CMS Integration | FramerSync',
    description:
      'Framer CMS integration for API, Airtable, Sheets, and Notion workflows with preview, mapping, automation, and run visibility.',
    h1: 'Framer CMS integration for external content systems',
    intro: {
      title: 'Connect Framer CMS to the systems your team already uses',
      paragraphs: [
        'Framer CMS works well as a presentation layer, but many teams manage the underlying content in Airtable, spreadsheets, internal tools, and other databases.',
        'FramerSync provides the integration layer that keeps Framer CMS aligned with those upstream systems.',
      ],
    },
    problem: {
      title: 'Why integration quality matters',
      paragraphs: [
        'A Framer CMS integration is not useful if it cannot be audited, re-run, or adjusted when field types and payload structures change.',
      ],
      bullets: [
        'Upstream systems change independently of the site.',
        'Marketing and ops teams need reliable sync visibility.',
        'Publishing workflows need repeatability, not a brittle connector.',
      ],
    },
    solution: {
      title: 'Use one integration workflow across multiple source types',
      paragraphs: [
        'FramerSync supports REST APIs, Airtable, Google Sheets, and Notion with a shared setup pattern that includes testing, preview, field mapping, and automation.',
      ],
      bullets: [
        'Multiple supported source types',
        'Collection-specific config management',
        'Webhook and schedule automation support',
        'Run status and history',
      ],
    },
    steps: [
      {
        title: 'Choose the source system',
        description:
          'Select the API or connected source that should act as the system of record for the Framer collection you are configuring.',
      },
      {
        title: 'Validate and map the content model',
        description:
          'Preview the source records, align them with the Framer fields you need, and save the configuration only after the mapping is clear.',
      },
      {
        title: 'Operate the integration over time',
        description:
          'Launch syncs, review run history, and move into automation when the integration is ready for repeat updates.',
      },
    ],
    cta: {
      title: 'Build a production-ready Framer CMS integration',
      description:
        'Set up the integration around the actual source of truth your team maintains and keep Framer aligned over time.',
      buttonLabel: 'See how it works',
      buttonHref: '/',
    },
    faqs: [
      {
        id: 'integration-faq-1',
        question: 'Which source systems can feed a Framer CMS integration?',
        answer:
          'FramerSync supports REST APIs, Airtable, Google Sheets, and Notion as upstream systems for Framer CMS collections.',
      },
      {
        id: 'integration-faq-2',
        question: 'Can each Framer collection have its own integration settings?',
        answer:
          'Yes. Each collection keeps its own configuration, field mapping, and automation settings so different content sources can be managed independently.',
      },
    ],
    relatedPaths: ['/framer-api-sync', '/sync-airtable-to-framer', '/sync-google-sheets-to-framer'],
  },
  '/sync-notion-to-framer': {
    path: '/sync-notion-to-framer',
    primaryKeyword: 'sync Notion to Framer',
    title: 'Sync Notion to Framer | FramerSync',
    description:
      'Sync Notion databases to Framer CMS with OAuth connection, property mapping, previews, schedules, and webhook automation for ongoing content publishing.',
    h1: 'Sync Notion databases to Framer CMS without copy-paste',
    intro: {
      title: 'Keep the database your team already edits in Framer',
      paragraphs: [
        'Notion is where a lot of teams already run their blog backlog, case-study library, team directory, and launch roadmap. Moving that content into Framer CMS by hand defeats the point of keeping it in Notion in the first place.',
        'FramerSync connects a Notion workspace to Framer CMS so a database you keep current in Notion automatically feeds the matching Framer collection on your schedule or when a webhook fires.',
      ],
    },
    problem: {
      title: 'Why Notion-to-Framer usually becomes a bottleneck',
      paragraphs: [
        'Notion property types are rich but do not map one-to-one onto Framer CMS fields, so teams end up rebuilding content in Framer or maintaining two copies in parallel.',
      ],
      bullets: [
        'Manual republish cycles miss updates the editorial team already made in Notion.',
        'Rich-text, multi-select, and relation properties are easy to translate wrong.',
        'Nothing records what changed or when a previous sync happened.',
      ],
    },
    solution: {
      title: 'Use FramerSync as the Notion-to-Framer pipeline',
      paragraphs: [
        'Authorize the Notion workspace, pick the database that backs a given Framer collection, preview a few rows, and map each property to the Framer field that should hold it.',
      ],
      bullets: [
        'OAuth connection to your Notion workspace',
        'Database selection and property-type-aware mapping',
        'Rich text, dates, numbers, selects, and relations supported',
        'Manual, scheduled, or webhook-triggered runs with full run history',
      ],
    },
    steps: [
      {
        title: 'Connect your Notion workspace',
        description:
          'Authorize FramerSync as an integration in the Notion workspaces you want to pull content from, and grant access to the databases that should feed Framer.',
      },
      {
        title: 'Map Notion properties to Framer fields',
        description:
          'Pick the database, preview a few rows, and line up Notion properties with the matching Framer CMS fields. Unsupported or empty cells default safely.',
      },
      {
        title: 'Schedule or webhook-trigger the sync',
        description:
          'Run the sync once to confirm the shape, then turn on scheduled automation or wire a webhook so Framer stays current without manual pushes.',
      },
    ],
    cta: {
      title: 'Start syncing Notion to Framer',
      description:
        'Connect Notion, choose the database, and configure the Framer CMS collection that should stay current with every edit.',
      buttonLabel: 'Create your account',
      buttonHref: '/register',
    },
    faqs: [
      {
        id: 'notion-faq-1',
        question: 'Does FramerSync handle Notion rich-text properties?',
        answer:
          'Yes. Rich-text and title properties flatten to the plain-text string Framer expects, preserving the readable content without the nested rich-text structure.',
      },
      {
        id: 'notion-faq-2',
        question: 'What about relations, multi-selects, and formulas?',
        answer:
          'Relations surface as arrays of related page IDs, multi-selects as arrays of strings, and formulas resolve to the computed value. Each maps cleanly into Framer fields of matching type.',
      },
      {
        id: 'notion-faq-3',
        question: 'Can I use views or filters from Notion?',
        answer:
          'FramerSync queries the database directly for the full result set rather than a Notion view. If you need a subset, filter on a property (for example, a Published checkbox) inside Notion and use a workflow that only sets it once the content is ready to publish.',
      },
    ],
    relatedPaths: ['/framer-cms-integration', '/how-to-sync-data-to-framer'],
  },
  '/framer-cms-webhook-sync': {
    path: '/framer-cms-webhook-sync',
    primaryKeyword: 'Framer CMS webhook sync',
    title: 'Framer CMS Webhook Sync | FramerSync',
    description:
      'Trigger instant Framer CMS updates from Zapier, Make, your own backend, or any HMAC-signed webhook. Purpose-built for event-driven publishing workflows.',
    h1: 'Trigger Framer CMS syncs from any webhook',
    intro: {
      title: 'Event-driven publishing instead of polling',
      paragraphs: [
        'Scheduled syncs are great when you do not know when content will change. When you do — a Stripe customer upgrades, a Zapier automation finishes, a backend publishes a new post — polling on a cron is wasted work and stale data.',
        'FramerSync exposes a signed webhook endpoint per collection so any upstream system can kick off a Framer CMS sync the moment the source data actually changes.',
      ],
    },
    problem: {
      title: 'Why polling alone is not enough',
      paragraphs: [
        'A 15-minute sync cadence means content lives in a 0-to-15-minute window of staleness. That is fine for a weekly launch calendar and wrong for anything customer-facing or revenue-linked.',
      ],
      bullets: [
        'Customer-visible data goes stale between scheduled runs.',
        'Rate-limited sources waste their quota checking for changes that did not happen.',
        'External systems that already know "something changed" have no way to tell Framer.',
      ],
    },
    solution: {
      title: 'Wire any emitter into a signed FramerSync webhook',
      paragraphs: [
        'Each Framer collection that has webhook automation turned on gets its own HTTPS endpoint and HMAC secret. Post to that endpoint with the correct signature header and FramerSync starts a sync immediately — no queue, no 15-minute wait.',
      ],
      bullets: [
        'Per-collection endpoints with rotatable secrets',
        'HMAC SHA-256 signatures with a replay-protection timestamp window',
        'Ready to wire into Zapier, Make, n8n, or any HTTP client',
        'Full run history so you can see exactly which webhook fired which sync',
      ],
    },
    steps: [
      {
        title: 'Turn on webhook automation for a collection',
        description:
          'Enable webhook triggers in the automation tab, copy the endpoint URL, and store the signing secret securely in your webhook emitter.',
      },
      {
        title: 'Post a signed request from your source of truth',
        description:
          'Generate an HMAC signature with the secret and current timestamp, include both headers, and POST to the FramerSync endpoint from Zapier, a serverless function, or your backend.',
      },
      {
        title: 'Watch the run history',
        description:
          'Every webhook-triggered sync shows up in run history with its trigger source, duration, counts, and any per-item errors so you can tune the integration over time.',
      },
    ],
    cta: {
      title: 'Wire your backend to Framer CMS',
      description:
        'Stop polling for changes that have not happened. Trigger Framer CMS syncs the instant your upstream data actually moves.',
      buttonLabel: 'Create your account',
      buttonHref: '/register',
    },
    faqs: [
      {
        id: 'webhook-faq-1',
        question: 'How do I sign a FramerSync webhook request?',
        answer:
          'Compute HMAC SHA-256 of `${timestamp}.${rawBody}` using your collection\'s webhook secret, then send the result hex-encoded in the x-anysync-signature header along with x-anysync-timestamp set to the Unix epoch seconds.',
      },
      {
        id: 'webhook-faq-2',
        question: 'What happens if two webhooks fire at the same time?',
        answer:
          'FramerSync coordinates all syncs per project through a single Durable Object, so concurrent webhooks are serialised into a queue rather than kicking off parallel writes that could collide in Framer.',
      },
      {
        id: 'webhook-faq-3',
        question: 'Is there a size limit on webhook request bodies?',
        answer:
          'Yes. Webhook bodies are capped at 256 KiB, which is many multiples of what real signed-envelope payloads use. Oversized requests are rejected with a 413 response before the signature check even runs.',
      },
    ],
    relatedPaths: ['/scheduled-framer-cms-updates', '/how-to-sync-data-to-framer', '/framer-cms-integration'],
  },
  '/scheduled-framer-cms-updates': {
    path: '/scheduled-framer-cms-updates',
    primaryKeyword: 'scheduled Framer CMS updates',
    title: 'Scheduled Framer CMS Updates | FramerSync',
    description:
      'Run Framer CMS syncs automatically on an interval or cron schedule. Keep content fresh without manual pushes and without writing a cron job yourself.',
    h1: 'Keep Framer CMS fresh on autopilot',
    intro: {
      title: 'A scheduled pipeline built for Framer',
      paragraphs: [
        'Running a scheduled job against Framer usually means standing up a cron worker, wiring it into Framer\'s API, handling auth, catching failures, and retrying. That is a weekend lost before the first sync actually runs.',
        'FramerSync is the scheduled pipeline so you do not have to build one. Pick an interval or drop in a cron expression and your Framer CMS refreshes on the cadence your content needs.',
      ],
    },
    problem: {
      title: 'Why rolling your own scheduler never stays done',
      paragraphs: [
        'Cron jobs are deceptively simple until the first failure. Then it is retry logic, backoff, deduplication, error visibility, credentials rotation, and a dashboard to know if anything actually ran.',
      ],
      bullets: [
        'Home-grown schedulers fail silently when they fail most — overnight, on weekends, during launches.',
        'Framer API errors need to be surfaced to the person who can act on them.',
        'Every new source, collection, or content type multiplies the cron footprint.',
      ],
    },
    solution: {
      title: 'Hand the cadence to FramerSync',
      paragraphs: [
        'Configure a schedule per collection — every minute, every hour, every morning, or a custom cron expression — and FramerSync runs the sync with the same mapping, preview, and error handling you already use.',
      ],
      bullets: [
        'Interval schedules (minute-level minimum) or full cron support',
        'Per-collection scheduling, so different content cadences coexist',
        'Automatic 429 detection and retry-after respect',
        'Run history showing every scheduled run, its counts, and any errors',
      ],
    },
    steps: [
      {
        title: 'Pick a source and map fields',
        description:
          'Connect your REST API, Airtable base, Google Sheets spreadsheet, or Notion database, and map the fields you want to keep current.',
      },
      {
        title: 'Choose an interval or cron expression',
        description:
          'Set a simple interval (every 5 minutes, every hour) or drop in a cron expression for calendar-aligned cadences like 8am weekdays.',
      },
      {
        title: 'Monitor run history',
        description:
          'Every scheduled run appears in the dashboard with duration, created/updated/deleted counts, and any error category so you can catch issues before your readers do.',
      },
    ],
    cta: {
      title: 'Put Framer CMS on a schedule',
      description:
        'Give your CMS a cadence that matches how often your source data actually changes, without building a scheduler yourself.',
      buttonLabel: 'Create your account',
      buttonHref: '/register',
    },
    faqs: [
      {
        id: 'schedule-faq-1',
        question: 'What is the minimum sync interval?',
        answer:
          'Every minute. For most collections a wider interval is more appropriate, but minute-level cadence is supported when the source genuinely changes that fast.',
      },
      {
        id: 'schedule-faq-2',
        question: 'Can I use a cron expression?',
        answer:
          'Yes. Drop any standard five-field cron expression into the schedule configuration for calendar-aligned cadences like Mondays at 8am, first of the month, or quarter-hourly business hours only.',
      },
      {
        id: 'schedule-faq-3',
        question: 'What happens if a scheduled run overlaps with a previous one still running?',
        answer:
          'Only one sync per collection runs at a time. If a scheduled trigger arrives while the previous run is still in flight, FramerSync reuses the active run instead of starting a duplicate, which protects your Framer CMS from conflicting writes.',
      },
    ],
    relatedPaths: ['/framer-cms-webhook-sync', '/how-to-sync-data-to-framer', '/framer-cms-integration'],
  },
  '/framer-cms-sync-vs-manual-csv-import': {
    path: '/framer-cms-sync-vs-manual-csv-import',
    primaryKeyword: 'Framer CMS sync vs manual CSV import',
    title: 'FramerSync vs Manual CSV Import | FramerSync',
    description:
      'An honest comparison of FramerSync and manual CSV imports for Framer CMS. When each makes sense, what they cost, and what breaks first at scale.',
    h1: 'FramerSync vs manual CSV imports for Framer CMS',
    intro: {
      title: 'Two ways to keep Framer CMS current — honestly compared',
      paragraphs: [
        'Every Framer project that depends on external data eventually picks a strategy. The two most common are manual CSV import and a sync tool like FramerSync. Both work; they break at different scales and cost different kinds of time.',
        'This page covers where each strategy wins, the exact failure modes of the alternative, and when the switch-over usually happens.',
      ],
    },
    problem: {
      title: 'Where manual CSV imports start to hurt',
      paragraphs: [
        'CSV import is free and obvious. For a one-off content migration or a site that updates weekly, it is a perfectly good choice. The pain starts when updates become routine.',
      ],
      bullets: [
        'Every edit forces an export-download-re-upload loop, so small changes still cost 10-15 minutes.',
        'Nobody is sure which row changed, so the "did it ship?" check becomes a visual diff.',
        'Schema changes in the source (new columns, renamed columns) break the CSV mapping silently.',
        'There is no run history — if a teammate re-imports an older version, the newer content quietly disappears.',
        'Multiple editors trying to refresh Framer at the same time overwrite each other\'s work.',
      ],
    },
    solution: {
      title: 'What FramerSync adds on top',
      paragraphs: [
        'FramerSync is a scheduled and event-driven pipeline with the mapping and review flow you already want from CSV, plus the operational visibility CSV does not give you.',
      ],
      bullets: [
        'Source connection once; updates flow automatically after that.',
        'Field mapping with preview so shape mismatches surface before content lands in Framer.',
        'Scheduled, webhook, or manual triggers — choose the right cadence per collection.',
        'Run history with counts, duration, and any per-item errors.',
        'Partial-failure safety: existing CMS data is preserved when a fetch fails, so readers never see empty content.',
      ],
    },
    steps: [
      {
        title: 'Keep CSV for one-shot migrations',
        description:
          'Manual CSV import is still the right tool for a one-time content migration, a small site that genuinely updates once a month, or an ad-hoc data cleanup.',
      },
      {
        title: 'Switch when updates become repetitive',
        description:
          'When the same team member is re-importing the same CSV more than twice a week, or when the source is already live in Airtable, Sheets, Notion, or an API, FramerSync almost always pays back its setup time in the first week.',
      },
      {
        title: 'Start with one collection',
        description:
          'Pick the collection that currently hurts the most — usually the one that needs to stay fresh for readers — and set up FramerSync against its real source. Keep the others on CSV until you are ready.',
      },
    ],
    cta: {
      title: 'Ready to stop exporting CSVs?',
      description:
        'Connect your existing source of truth once, and let Framer CMS stay current on the cadence your team already works at.',
      buttonLabel: 'Create your account',
      buttonHref: '/register',
    },
    faqs: [
      {
        id: 'csv-faq-1',
        question: 'Is manual CSV import ever the right choice?',
        answer:
          'Yes. For one-shot migrations, ad-hoc data cleanup, or a site that genuinely changes less than once a month, CSV is free and obvious. The cost of a sync tool only pays off when updates are routine.',
      },
      {
        id: 'csv-faq-2',
        question: 'Can I use FramerSync alongside manual CSV import?',
        answer:
          'Yes. Some collections can stay on manual CSV while others use FramerSync. The two do not conflict — FramerSync only writes to the collections you have configured.',
      },
      {
        id: 'csv-faq-3',
        question: 'What happens if my source schema changes?',
        answer:
          'FramerSync surfaces the new columns during the next sync preview and you decide whether to map them. Mappings that reference columns that no longer exist produce a clear per-field warning instead of silently dropping data.',
      },
    ],
    relatedPaths: ['/framer-cms-integration', '/how-to-sync-data-to-framer'],
  },
  '/framer-cms-sync-vs-zapier': {
    path: '/framer-cms-sync-vs-zapier',
    primaryKeyword: 'Framer CMS sync vs Zapier',
    title: 'FramerSync vs Zapier for Framer CMS | FramerSync',
    description:
      'An honest comparison of FramerSync and Zapier for Framer CMS updates. Where Zapier wins, where a purpose-built sync tool wins, and how to choose.',
    h1: 'FramerSync vs Zapier for keeping Framer CMS current',
    intro: {
      title: 'Two general approaches, one with Framer as a first-class citizen',
      paragraphs: [
        'Zapier is a general-purpose automation platform that can technically push data into Framer CMS through HTTP actions or a custom app. FramerSync is a purpose-built sync tool for Framer specifically.',
        'Both paths work. They have different strengths, different tradeoffs, and a predictable switch-over point. This page is an honest breakdown so you can pick the right tool for your setup rather than over-buying or over-hacking.',
      ],
    },
    problem: {
      title: 'Where Zapier fits, and where it starts to strain',
      paragraphs: [
        'Zapier is great when a single event should trigger a single action — a Typeform submission creates a Framer CMS row, a new Stripe customer updates a field, a Slack message tags a post. That is exactly what it was built for.',
        'It strains when the job becomes "keep Framer CMS aligned with an external system of truth" because Zapier is optimised for event-by-event work, not collection-level state.',
      ],
      bullets: [
        'Zap runs are charged per task, so a nightly sync of 500 rows can burn through a plan quota fast.',
        'There is no collection-level diff: updates and deletes have to be orchestrated across multiple zaps, each handling one record type of change.',
        'Field mapping lives inside Zap configuration, which is harder to review and version than a dedicated mapping UI.',
        'No per-run history specific to Framer — you see Zap task logs, not a clean "this collection synced, N created, N updated, 1 failed because of X" summary.',
      ],
    },
    solution: {
      title: 'What FramerSync brings that Zapier is not optimised for',
      paragraphs: [
        'FramerSync is scoped to the Framer CMS sync problem specifically. That scope lets it do things Zapier cannot do cleanly without meaningful workflow gymnastics.',
      ],
      bullets: [
        'Fetch, diff, and write as one atomic pass per run, not N individual record zaps.',
        'Purpose-built preview + mapping UI for Framer CMS field types (rich text, arrays, images, dates).',
        'Per-collection schedules, webhooks, and run history without composing three Zaps to get visibility.',
        'Partial-failure safety — an upstream 429 or timeout aborts the run before writes, so your Framer site keeps serving the last good snapshot.',
        'Flat pricing rather than per-task metering, so a busy daily sync does not scale the bill linearly.',
      ],
    },
    steps: [
      {
        title: 'Keep Zapier for event-by-event triggers',
        description:
          'Zapier is still the right tool when a single event in one app should produce a single action in another, especially for workflows that span ten different SaaS tools none of which are Framer.',
      },
      {
        title: 'Use FramerSync when Framer CMS is the destination',
        description:
          'As soon as the work becomes "keep this collection in sync with a source of truth" — Airtable, Google Sheets, Notion, a REST API — FramerSync is the purpose-built tool and Zapier is the workaround.',
      },
      {
        title: 'You can absolutely use both',
        description:
          'A common setup: Zapier handles app-to-app automation (new Stripe customer posts to Slack, Typeform submission updates a HubSpot contact), and FramerSync handles CMS sync. The two are complementary, not alternatives.',
      },
    ],
    cta: {
      title: 'Keep Framer CMS on a purpose-built pipeline',
      description:
        'If you already have a source of truth and want Framer to reflect it cleanly, FramerSync is the shortest path from "data lives there" to "CMS is up to date".',
      buttonLabel: 'Create your account',
      buttonHref: '/register',
    },
    faqs: [
      {
        id: 'zapier-faq-1',
        question: 'Can I trigger FramerSync from Zapier?',
        answer:
          'Yes. Every collection with webhook automation enabled has a signed HTTP endpoint. Zapier can POST to that endpoint as the final step of a Zap, which gives you Zapier\'s trigger catalog plus FramerSync\'s sync-and-diff engine as the actual writer to Framer.',
      },
      {
        id: 'zapier-faq-2',
        question: 'Is FramerSync cheaper than Zapier for CMS sync?',
        answer:
          'For ongoing CMS sync the answer is usually yes, because FramerSync is flat-rate rather than per-task. A Zap that syncs 200 rows every hour will consume 200 × 24 tasks a day; the same workload on FramerSync is a single scheduled run.',
      },
      {
        id: 'zapier-faq-3',
        question: 'What about custom Zaps using Framer\'s API directly?',
        answer:
          'Possible, but you are rebuilding the diff, preview, mapping, error handling, and rate-limit logic that FramerSync already provides — and maintaining it forever. Worth considering only if your workflow is genuinely novel and not a collection-sync problem.',
      },
    ],
    relatedPaths: [
      '/framer-cms-sync-vs-manual-csv-import',
      '/framer-cms-webhook-sync',
      '/framer-cms-integration',
    ],
  },
};

export const seoLandingPages = Object.values(seoLandingPageRegistry);
